import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiChevronDown, FiChevronUp, FiRefreshCw, FiAlertCircle } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import EpisodeSkeleton from './EpisodeSkeleton';
import SubscribeLinks from './SubscribeLinks';
import PodcastHeader from './PodcastHeader';
import { fetchPodcastFeed } from '../utils/rssParser';
import { useAudio } from '../context/AudioContext';

const EpisodeList = ({ feedUrl }) => {
  const [allEpisodes, setAllEpisodes] = useState([]);
  const [channelData, setChannelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  
  const { playEpisode, currentEpisode, isPlaying } = useAudio();
  const mounted = useRef(true);
  const loadedRef = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const loadFeed = () => {
    if (!mounted.current) return;
    setLoading(true);
    setError(null);
    loadedRef.current = false;

    fetchPodcastFeed(feedUrl, (data) => {
      if (!mounted.current) return;
      if (data?.episodes?.length > 0) {
        setAllEpisodes(data.episodes);
        if (data.channel?.title) setChannelData(data.channel);
        setLoading(false);
        loadedRef.current = true;
      }
    })
    .then((data) => {
      if (!mounted.current) return;
      if (data?.episodes?.length > 0) {
        setAllEpisodes(data.episodes);
        if (data.channel?.title) setChannelData(data.channel);
        setLoading(false);
        loadedRef.current = true;
      } else if (!loadedRef.current) {
        setError('No valid episodes found in this feed.');
        setLoading(false);
      }
    })
    .catch((err) => {
      if (!mounted.current) return;
      if (!loadedRef.current) {
        setError(err.message || 'Could not load podcast feed.');
        setLoading(false);
      }
    });
  };

  // Reload feed if URL parameter changes
  useEffect(() => { 
    loadFeed(); 
  }, [feedUrl]);

  const latestEpisode = allEpisodes[0] || null;
  const olderEpisodes = allEpisodes.slice(1);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Dynamic Header */}
      <PodcastHeader channel={channelData} />

      {/* List Header */}
      <div className="flex items-end justify-between mb-1 px-1">
        <div>
          <h2 className="text-xl font-bold text-brand-dark font-serif">Latest Episodes</h2>
          <p className="text-[10px] text-brand-green/50 uppercase tracking-widest font-bold mt-0.5">
            {loading ? 'Downloading feed…' : allEpisodes.length > 0 ? `Showing ${allEpisodes.length} episodes` : 'No episodes'}
          </p>
        </div>
        {error && !loading && (
          <button
            onClick={loadFeed}
            className="text-[10px] font-bold uppercase tracking-widest text-red-400 hover:text-brand-tan transition-colors flex items-center gap-1"
          >
            <SafeIcon icon={FiRefreshCw} className="w-3 h-3" /> Retry
          </button>
        )}
      </div>

      <SubscribeLinks />

      <div className="mt-4 flex flex-col gap-3">
        {loading ? (
          <>
            <EpisodeSkeleton />
            <EpisodeSkeleton />
            <EpisodeSkeleton />
          </>
        ) : error && allEpisodes.length === 0 ? (
          <ErrorState error={error} feedUrl={feedUrl} onRetry={loadFeed} />
        ) : latestEpisode ? (
          <>
            <motion.div
              key={latestEpisode.guid || latestEpisode.audioUrl}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <LatestEpisodeCard
                episode={latestEpisode}
                onPlay={() => playEpisode(latestEpisode, allEpisodes)}
                isPlayingEpisode={currentEpisode?.audioUrl === latestEpisode.audioUrl && isPlaying}
              />
            </motion.div>

            {olderEpisodes.length > 0 && (
              <>
                <AnimatePresence>
                  {showAll && (
                    <motion.div
                      key="older-list"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex flex-col gap-2 overflow-hidden"
                    >
                      {olderEpisodes.map((ep, idx) => (
                        <motion.div
                          key={ep.guid || ep.audioUrl}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.2, delay: idx * 0.04 }}
                        >
                          <EpisodeRow
                            episode={ep}
                            onPlay={() => playEpisode(ep, allEpisodes)}
                            isPlayingEpisode={currentEpisode?.audioUrl === ep.audioUrl && isPlaying}
                          />
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  onClick={() => setShowAll((p) => !p)}
                  className="w-full flex items-center justify-center gap-2 py-3 text-[10px] font-bold uppercase tracking-widest text-brand-green/60 hover:text-brand-tan transition-colors border border-brand-green/10 rounded-lg bg-white hover:border-brand-tan/30"
                >
                  <SafeIcon icon={showAll ? FiChevronUp : FiChevronDown} className="w-3.5 h-3.5" />
                  {showAll ? 'Show Latest' : `See ${olderEpisodes.length} More Episodes`}
                </button>
              </>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

const ErrorState = ({ error, feedUrl, onRetry }) => (
  <div className="text-center py-10 bg-white rounded-xl border border-red-100 px-6">
    <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-300 mx-auto mb-3" />
    <p className="text-sm font-serif italic text-brand-dark mb-1">Could not load episodes</p>
    <p className="text-[10px] text-brand-green/40 mb-2 max-w-xs mx-auto">{error}</p>
    <p className="text-[9px] text-brand-green/30 mb-5 break-all font-mono bg-brand-cream p-2 rounded max-w-md mx-auto">
      {feedUrl}
    </p>
    <button
      onClick={onRetry}
      className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-brand-tan hover:text-brand-dark transition-colors border border-brand-tan/30 rounded-full px-4 py-1.5"
    >
      <SafeIcon icon={FiRefreshCw} className="w-3 h-3" /> Try Again
    </button>
  </div>
);

const LatestEpisodeCard = ({ episode, onPlay, isPlayingEpisode }) => (
  <div className={`bg-white rounded-xl border overflow-hidden shadow-sm transition-all duration-300 ${isPlayingEpisode ? 'border-brand-tan ring-1 ring-brand-tan/20' : 'border-brand-green/10'}`}>
    <div className="flex gap-4 p-4">
      <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-brand-green/10 shadow">
        {episode.image
          ? <img src={episode.image} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-brand-green/20 flex items-center justify-center text-brand-green/40 text-2xl">🎙</div>
        }
        {isPlayingEpisode && (
          <div className="absolute inset-0 bg-brand-dark/40 flex items-center justify-center">
            <span className="text-white text-xs font-bold">▶</span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[9px] font-bold uppercase tracking-widest text-brand-tan bg-brand-tan/10 px-2 py-0.5 rounded">
            Latest
          </span>
          {isPlayingEpisode && (
            <span className="text-[9px] font-bold uppercase tracking-widest text-green-600 bg-green-50 px-2 py-0.5 rounded animate-pulse">
              Now Playing
            </span>
          )}
        </div>
        <h3 className={`text-sm font-bold font-serif leading-snug line-clamp-2 mb-1 ${isPlayingEpisode ? 'text-brand-tan' : 'text-brand-dark'}`}>
          {episode.title}
        </h3>
        <p className="text-[10px] text-brand-green/60 font-medium uppercase tracking-wider">
          {episode.pubDate}{episode.duration ? ` · ${episode.duration}` : ''}
        </p>
      </div>
    </div>
    {episode.description && (
      <p className="px-4 pb-3 text-xs text-brand-green/70 leading-relaxed line-clamp-2">
        {episode.description}
      </p>
    )}
    <div className="border-t border-brand-green/10">
      <button
        onClick={onPlay}
        className={`w-full py-3 text-[11px] font-bold uppercase tracking-widest transition-all duration-200 flex items-center justify-center gap-2 ${isPlayingEpisode ? 'bg-brand-tan/10 text-brand-tan' : 'hover:bg-brand-dark hover:text-white text-brand-dark'}`}
      >
        {isPlayingEpisode ? '⏸ Pause' : '▶ Play Episode'}
      </button>
    </div>
  </div>
);

const EpisodeRow = ({ episode, onPlay, isPlayingEpisode }) => (
  <button
    onClick={onPlay}
    className={`w-full group flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 text-left ${isPlayingEpisode ? 'bg-brand-tan/10 border-brand-tan/30' : 'bg-white border-brand-green/10 hover:border-brand-tan/30 hover:shadow-sm'}`}
  >
    <div className="relative w-11 h-11 flex-shrink-0 rounded overflow-hidden bg-brand-green/10">
      {episode.image
        ? <img src={episode.image} alt="" loading="lazy" className="w-full h-full object-cover" />
        : <div className="w-full h-full bg-brand-green/20 flex items-center justify-center text-brand-green/40">🎙</div>
      }
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${isPlayingEpisode ? 'opacity-100 bg-brand-dark/30' : 'opacity-0 group-hover:opacity-100 bg-brand-dark/20'}`}>
        <div className="bg-brand-tan text-white rounded-full p-1">
          <span className="text-[8px] font-bold">{isPlayingEpisode ? '⏸' : '▶'}</span>
        </div>
      </div>
    </div>
    <div className="flex-1 min-w-0">
      <h4 className={`text-xs font-bold truncate font-serif ${isPlayingEpisode ? 'text-brand-tan' : 'text-brand-dark group-hover:text-brand-tan transition-colors'}`}>
        {episode.title}
      </h4>
      <p className="text-[10px] text-brand-green/50 uppercase tracking-wider mt-0.5">
        {episode.pubDate}{episode.duration ? ` · ${episode.duration}` : ''}
      </p>
    </div>
  </button>
);

export default EpisodeList;